import React, { useState } from "react";
import { Box, Button, TextField, Typography, Container, InputAdornment, IconButton, Link, Snackbar, Alert, useMediaQuery, useTheme } from "@mui/material";
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from "@mui/icons-material";

import { auth, db } from "../firebase/firebase";
import {createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const BACKGROUND_IMAGES = {
  mangrove: 'https://images.unsplash.com/photo-1714544566545-96da265563df?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Z3JlZW5lcnklMjBsYW5kc2NhcGV8ZW58MHx8MHx8fDA%3D',
};

const AuthPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone: "",
  });

  const currentBackgroundImage = BACKGROUND_IMAGES.mangrove;

  // Add animation state for mobile transitions
  const [mobileAnimating, setMobileAnimating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    // Validate email format
    if (!formData.email) {
      setError("Email is required");
      return false;
    }
    
    // Email validation (@)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    
    // Validate passwords match
    if (isSignUp && formData.password !== formData.confirmPassword) {
      setError("Passwords don't match");
      return false;
    }
    
    // Validate name (letters and spaces only)
    if (isSignUp && formData.name && !/^[a-zA-Z\s]*$/.test(formData.name)) {
      setError("Name should only contain letters");
      return false;
    }
    
    // Validate phone (digits, spaces, parentheses, plus, and hyphen only)
    if (isSignUp && formData.phone && !/^[0-9\s()+-]*$/.test(formData.phone)) {
      setError("Please enter a valid phone number");
      return false;
    }
    
    // Validate required fields for signup
    if (isSignUp && (!formData.name || !formData.email || !formData.password)) {
      setError("Please fill in all required fields");
      return false;
    }
    
    return true;
  };

  const toggleAuthMode = () => {
    if (isMobile) {
      // Use fade transition for mobile
      setMobileAnimating(true);
      setTimeout(() => {
        setIsSignUp(!isSignUp);
        setMobileAnimating(false);
      }, 300);
    } else {
      // Use existing slide transition for desktop
      setIsSignUp(!isSignUp);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      if (isSignUp) {
        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );

        // Create user document in Firestore
        await setDoc(doc(db, "users", userCredential.user.uid), {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || "",
          createdAt: serverTimestamp()
        });

        localStorage.setItem('isAuthenticated', 'true');
        setSuccessMessage(`Welcome, ${formData.name}! Your account has been created successfully.`);
        setSuccess(true);
        
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        await signInWithEmailAndPassword(auth, formData.email, formData.password);
        localStorage.setItem('isAuthenticated', 'true');
        setSuccessMessage("Successfully signed in.");
        setSuccess(true);
        
        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (err) {
      // Override Firebase error messages with user-friendly ones
      if (err.code === 'auth/invalid-email') {
        setError("Please enter a valid email address");
      } else if (err.code === 'auth/email-already-in-use') {
        setError("This email is already registered. Please use a different email or sign in");
      } else if (err.code === 'auth/user-not-found') {
        setError("No account found with this email address");
      } else if (err.code === 'auth/wrong-password') {
        setError("Incorrect password. Please try again");
      } else if (err.code === 'auth/weak-password') {
        setError("Password should be at least 6 characters");
      } else {
        setError("An error occurred. Please try again later");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate email
    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }
    
    if (!/^[a-zA-Z0-9@._-]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    setLoading(true);
    
    try {
      await sendPasswordResetEmail(auth, formData.email);
      setSuccessMessage("Password reset email sent! Check your inbox.");
      setSuccess(true);
      
      setTimeout(() => {
        setIsPasswordReset(false);
      }, 3000);
    } catch (err) {
      // Override Firebase error messages
      if (err.code === 'auth/user-not-found') {
        setError("No account found with this email address");
      } else if (err.code === 'auth/invalid-email') {
        setError("Please enter a valid email address");
      } else {
        setError("An error occurred. Please try again later");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccess = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSuccess(false);
  };

  // Mobile-specific rendering
  if (isMobile) {
    return (
      <Container 
        maxWidth={false}
        disableGutters
        sx={{
          minHeight: "calc(100vh - 64px)",
          width: "100%",
          display: "flex", 
          flexDirection: "column",
          position: "relative",
          opacity: mobileAnimating ? 0.5 : 1,
          transition: "opacity 0.3s ease",
          overflow: "hidden", // Prevent horizontal scrolling
        }}
      >
        {/* Background image at top */}
        <Box
          sx={{
            height: "30vh",
            width: "100%",
            backgroundImage: `url(${currentBackgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Overlay same as before */} 
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: 2,
            }}
          >
            <Typography
              variant="h5"
              color="white"
              fontWeight="bold"
              align="center"
              sx={{
                textShadow: "0px 2px 4px rgba(0,0,0,0.3)", // Add shadow for better readability
                px: 1,
                maxWidth: "95%", // Prevent text from overflowing on extra small screens
              }}
            >
              {isSignUp ? "SIGN UP NOW AND DISCOVER MORE FEATURES" : "SIGN IN TO UNLOCK MORE FEATURES"}
            </Typography>
          </Box>
        </Box>
        
        {/* Form container with shadow for separation */}
        <Box 
          sx={{ 
            flex: 1,
            width: "100%",
            px: { xs: 2, sm: 3 },
            py: 4, 
            mt: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            bgcolor: "white",
            boxSizing: "border-box",
            boxShadow: "0px -4px 10px rgba(0,0,0,0.05)", // Subtle shadow at top
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ 
            width: "100%", 
            maxWidth: { xs: "100%", sm: 400 }, // Full width on xs, max 400px on sm+ 
            boxSizing: "border-box", 
          }}>
            {/* Form Title */}
            {isSignUp ? (
              <Typography 
                variant="h4" 
                align="center"
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: 28, sm: 36 }, // Smaller on tiny screens
                  color: "black", 
                  mb: 3,
                  fontFamily: "Inter, sans-serif",
                }}
              >
                SIGN UP
              </Typography>
            ) : isPasswordReset ? (
              <Typography 
                variant="h4" 
                align="center"
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: 28, sm: 36 },
                  color: "black", 
                  mb: 3,
                  fontFamily: "Inter, sans-serif",
                }}
              >
                RESET PASSWORD
              </Typography>
            ) : (
              <Box sx={{ mb: 3, textAlign: "center" }}>
                <Typography 
                  variant="h4"
                  align="center"
                  sx={{ 
                    fontWeight: 800,
                    fontSize: { xs: 32, sm: 40 }, 
                    color: "black", 
                    fontFamily: "Inter, sans-serif",
                    lineHeight: 1.1,
                  }}
                >
                  WELCOME BACK
                </Typography>
              </Box>
            )}
            
            {/* Form - Ensuring they don't overflow */}
            <form style={{ width: "100%", boxSizing: "border-box" }} onSubmit={isPasswordReset ? handlePasswordReset : handleSubmit}>
              {/* Email field */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                  Email
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { borderRadius: 1 },
                  }}
                />
              </Box>
              
              {isPasswordReset && (
                <Box sx={{ mb: 3, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" align="left">
                    Enter your email address and we'll send you a link to reset your password.
                    <br /><br />
                    <strong>Important:</strong> If you don't see the email in your inbox,
                    please check your spam/junk folder.
                  </Typography>
                </Box>
              )}
              
              {/* Password field */}
              {!isPasswordReset && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                    Password
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { borderRadius: 1 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={togglePasswordVisibility} edge="end" size="small">
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
              
              {/* Sign up fields */}
              {isSignUp && !isPasswordReset && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                    Confirm Password
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Confirm your password"
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { borderRadius: 1 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={togglePasswordVisibility} edge="end" size="small">
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                    Name
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { borderRadius: 1 },
                    }}
                  />

                  <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                    Phone
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your phone number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { borderRadius: 1 },
                    }}
                  />
                </Box>
              )}
              
              {/* Forgot password link */}
              {!isSignUp && !isPasswordReset && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Link 
                    href="#" 
                    variant="body2" 
                    underline="hover" 
                    color="black" 
                    sx={{ fontWeight: 'bold' }}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPasswordReset(true);
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box>
              )}
              
              {/* Error message */}
              {error && (
                <Typography color="error" sx={{ mt: 1, mb: 1 }}>
                  {error}
                </Typography>
              )}
              
              {/* Submit button */}
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  mt: 1,
                  mb: 2,
                  py: 1.25,
                  bgcolor: '#000',
                  color: 'white',
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#333',
                  }
                }}
              >
                {isPasswordReset 
                  ? (loading ? "Sending..." : "Send Reset Link") 
                  : (isSignUp 
                      ? (loading ? "Creating Account..." : "Sign Up") 
                      : (loading ? "Signing In..." : "Sign in")
                    )
                }
              </Button>
              
              {/* Return to sign in */}
              {isPasswordReset && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Remember your password?{" "}
                    <Link
                      href="#"
                      underline="hover"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsPasswordReset(false);
                      }}
                      sx={{ color: '#ff0000', fontWeight: 'bold' }}
                    >
                      Back to Sign in
                    </Link>
                  </Typography>
                </Box>
              )}
              
              {/* Switch between sign up and sign in */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {isSignUp ? "Already have an account? " : "Don't have an account? "}
                  <Link
                    href="#"
                    underline="hover"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleAuthMode();
                    }}
                    sx={{ color: '#ff0000', fontWeight: 'bold' }}
                  >
                    {isSignUp ? "Sign in here" : "Sign up for free"}
                  </Link>
                </Typography>
              </Box>
            </form>
          </Box>
        </Box>
        
        {/* Success notification */}
        <Snackbar 
          open={success} 
          autoHideDuration={4000} 
          onClose={handleCloseSuccess}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSuccess} 
            severity="success" 
            variant="filled"
            sx={{ 
              width: { xs: "90%", sm: "auto" }, // Responsive width for alert
              maxWidth: "90vw", // Prevent overflow
              boxShadow: 3, 
              fontWeight: 'bold',
              fontSize: { xs: "0.875rem", sm: "1rem" }
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  // Desktop layout - unchanged from your original code
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        height: "calc(100vh - 64px)",
        width: "100%",
        display: "flex",
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Form Area */}
      <Box
        sx={{
          width: "50%",
          height: "100%",
          bgcolor: "white",
          display: "flex",
          flexDirection: "column",
          justifyContent: isSignUp ? "flex-start" : "center",
          alignItems: "flex-start",
          paddingTop: isSignUp ? { xs: "3vh", md: "4vh" } : 0,
          paddingBottom: { xs: 2, md: 3 },
          paddingLeft: { xs: 3, md: 6 },
          paddingRight: { xs: 3, md: 6 },
          position: "absolute",
          zIndex: 2,
          left: isSignUp ? "50%" : "0",
          transition: "left 0.8s ease-in-out, padding-top 0.3s ease",
          overflowY: "auto",
        }}
      >
        {/* Your existing desktop form code */}
        <Box 
          sx={{ 
            width: "100%", 
            maxWidth: 450, 
            mx: "auto",
          }}
        >
          {/* Form Title */}
          {isSignUp ? (
            <Typography 
              variant="h4" 
              align="left"
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: 40, md: 48 },
                color: "black", 
                mb: 2,
                mt: 0,
                fontFamily: "Inter, sans-serif",
              }}
            >
              SIGN UP
            </Typography>
          ) : isPasswordReset ? (
            <Typography 
              variant="h4" 
              align="left"
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: 40, md: 48 },
                color: "black", 
                mb: 2,
                mt: 0,
                fontFamily: "Inter, sans-serif",
              }}
            >
              RESET PASSWORD
            </Typography>
          ) : (
            <Box sx={{ mb: 4, mt: 0, textAlign: "left" }}>
              <Typography 
                variant="h4"
                align="left"
                sx={{ 
                  fontWeight: 800,
                  fontSize: 52, 
                  color: "black", 
                  fontFamily: "Inter, sans-serif",
                  lineHeight: 1.1,
                }}
              >
                WELCOME
              </Typography>
              <Typography 
                variant="h4"
                align="left" 
                sx={{ 
                  fontWeight: 800,
                  fontSize: 52, 
                  color: "black", 
                  fontFamily: "Inter, sans-serif",
                }}
              >
                BACK
              </Typography>
            </Box>
          )}
          
          <form style={{ width: "100%" }} onSubmit={isPasswordReset ? handlePasswordReset : handleSubmit}>
            {/* Your existing form fields */}
            <Box sx={{ mb: 1, mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                Email
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { borderRadius: 1 },
                }}
              />
            </Box>
            
            {isPasswordReset && (
              <Box sx={{ mb: 3, mt: 1 }}>
                <Typography variant="body2" color="text.secondary" align="left">
                  Enter your email address and we'll send you a link to reset your password.
                  <br /><br />
                  <strong>Important:</strong> If you don't see the email in your inbox,
                  please check your spam/junk folder and mark it as "not spam" to
                  ensure you receive future emails.
                </Typography>
              </Box>
            )}
            
            {!isPasswordReset && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { borderRadius: 1 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end" size="small">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
            
            {isSignUp && !isPasswordReset && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                  Confirm Password
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Confirm your password"
                  name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { borderRadius: 1 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end" size="small">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                  Name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { borderRadius: 1 },
                  }}
                />

                <Typography variant="body2" color="text.secondary" gutterBottom align="left" sx={{ fontWeight: 700 }}>
                  Phone
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your phone number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { borderRadius: 1 },
                  }}
                />
              </Box>
            )}
            
            {!isSignUp && !isPasswordReset && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Link 
                  href="#" 
                  variant="body2" 
                  underline="hover" 
                  color="black" 
                  sx={{ fontWeight: 'bold' }}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsPasswordReset(true);
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>
            )}
            
            {error && (
              <Typography color="error" sx={{ mt: 1, mb: 1 }}>
                {error}
              </Typography>
            )}
            
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{
                mt: 1,
                mb: 2,
                py: 1.25,
                bgcolor: '#000',
                color: 'white',
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#333',
                }
              }}
            >
              {isPasswordReset 
                ? (loading ? "Sending..." : "Send Reset Link") 
                : (isSignUp 
                    ? (loading ? "Creating Account..." : "Sign Up") 
                    : (loading ? "Signing In..." : "Sign in")
                  )
              }
            </Button>
            
            {isPasswordReset && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Remember your password?{" "}
                  <Link
                    href="#"
                    underline="hover"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPasswordReset(false);
                    }}
                    sx={{ color: '#ff0000', fontWeight: 'bold' }}
                  >
                    Back to Sign in
                  </Link>
                </Typography>
              </Box>
            )}
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {isSignUp ? "Already have an account? " : "Don't have an account? "}
                <Link
                  href="#"
                  underline="hover"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleAuthMode();
                  }}
                  sx={{ color: '#ff0000', fontWeight: 'bold' }}
                >
                  {isSignUp ? "Sign in here" : "Sign up for free"}
                </Link>
              </Typography>
            </Box>
          </form>
        </Box>
      </Box>

      {/* Background with Wave */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          top: 0,
          left: 0,
          zIndex: 1,
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: isSignUp ? 0 : "50%",
            width: "50%",
            height: "100%",
            backgroundImage: `url(${currentBackgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transition: "left 0.8s ease-in-out",
            zIndex: 1,
          }}
        >
          {/* Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.4)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: 4,
              zIndex: 2,
            }}
          >
            <Typography
              variant="h3"
              color="white"
              fontWeight="bold"
              sx={{ maxWidth: 500, textAlign: "center", mb: 2 }}
            >
              {isSignUp ? "SIGN UP NOW AND DISCOVER MORE FEATURES" : "SIGN IN TO UNLOCK MORE FEATURES"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Snackbar 
        open={success} 
        autoHideDuration={4000} 
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccess} 
          severity="success" 
          variant="filled"
          sx={{ 
            width: { xs: "90%", sm: "auto" }, // Responsive width for alert
            maxWidth: "90vw", // Prevent overflow
            boxShadow: 3, 
            fontWeight: 'bold',
            fontSize: { xs: "0.875rem", sm: "1rem" }
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AuthPage;
